import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { type ApiError, api, unwrap } from '../api/client';
import { useAccounts, usePlans } from '../api/hooks';
import type { Billing, CheckoutResult, Workspace } from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  Card,
  DataTable,
  ErrorAlert,
  Field,
  fmtDate,
  PageHeader,
  useToast,
} from '../components/ui';
import { ChangePasswordCard } from './Password';

const PROVIDERS = [
  ['openai', 'OpenAI'],
  ['anthropic', 'Anthropic'],
  ['gemini', 'Google Gemini'],
  ['deepseek', 'DeepSeek'],
] as const;

export function SettingsPage() {
  const { me, refresh, can, logout } = useSession();
  const toast = useToast();
  const [tab, setTab] = useState<'account' | 'control' | 'safety' | 'ai'>('account');
  const ws = me?.workspace;
  const plans = usePlans();
  const accounts = useAccounts();
  const patch = useMutation({
    mutationFn: (body: Partial<Workspace>) =>
      unwrap(api.patch<Workspace>('/portal/settings', body)),
    onSuccess: async () => {
      await refresh();
      toast('Settings saved');
    },
  });
  const qc = useQueryClient();
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => unwrap(api.get<Billing>('/portal/billing')),
    enabled: can('billing:manage'),
  });
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const plan = useMutation({
    mutationFn: (plan: string) =>
      unwrap(api.post<CheckoutResult>('/portal/billing/checkout', { plan, interval })),
    onSuccess: async (r) => {
      if (r.redirectUrl) {
        window.location.assign(r.redirectUrl);
        return;
      }
      await refresh();
      await qc.invalidateQueries({ queryKey: ['billing'] });
      toast('Plan updated');
    },
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(api.post('/portal/billing/cancel')),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['billing'] });
      toast('Subscription cancelled; it stays active until the period end');
    },
  });
  const portal = useMutation({
    mutationFn: () => unwrap(api.get<{ url: string | null }>('/portal/billing/portal')),
    onSuccess: (r) => {
      if (r.url) window.location.assign(r.url);
      else toast('No payment portal in this environment', 'error');
    },
  });
  const sub = billing.data?.subscription ?? null;
  // UI-39: the provider wants the plan changed in its portal; say so and point there
  const planError = (plan.error as ApiError | null) ?? null;
  const usePortal = planError?.code === 'use_portal';
  const portalHint = usePortal
    ? ((planError?.details as { portalUrl?: string | null } | undefined)?.portalUrl ?? null)
    : null;
  // Safety tab: the same account actions as the Accounts page
  const health = useMutation({
    mutationFn: (id: string) => unwrap(api.post(`/portal/social-accounts/${id}/health`)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['accounts'] });
      toast('Connection tested');
    },
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => unwrap(api.post(`/portal/social-accounts/${id}/disconnect`)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['accounts'] });
      toast('Account disconnected');
    },
  });
  const [ai, setAi] = useState({ provider: ws?.aiProvider ?? 'openai', apiKey: '' });
  // IAM-12 UI-22: the owner deletes the organisation by typing its name
  const [confirmName, setConfirmName] = useState('');
  const destroy = useMutation({
    mutationFn: () => unwrap(api.del('/portal/workspace', { confirmName })),
    onSuccess: () => {
      toast('Workspace deleted');
      void logout().catch(() => undefined);
    },
  });
  const aiSave = useMutation({
    // AIR-06: a key stores and activates; a provider alone activates its stored key; null removes it
    mutationFn: (body: { provider: string; apiKey?: string | null }) =>
      unwrap(api.post('/portal/settings/ai', body)),
    onSuccess: async () => {
      await refresh();
      setAi((a) => ({ ...a, apiKey: '' }));
      toast('AI configuration saved');
    },
  });
  const [limit, setLimit] = useState(String(ws?.commentLimitPerHour ?? 0));
  if (!ws) return null;
  const toggle = (
    key: 'automationsEnabled' | 'commentRepliesEnabled' | 'dmRepliesEnabled',
    label: string,
  ) => (
    <label className="checkbox" key={key}>
      <input
        type="checkbox"
        checked={ws[key]}
        disabled={!can('settings:manage')}
        onChange={(e) => patch.mutate({ [key]: e.target.checked })}
      />{' '}
      {label}
    </label>
  );
  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Account, automation control, safety limits, AI configuration"
      />
      <div className="tabs">
        {(
          [
            ['account', 'Account'],
            ['control', 'Automation control'],
            ['safety', 'Safety & limits'],
            ['ai', 'AI configuration'],
          ] as const
        ).map(([k, l]) => (
          <button
            type="button"
            key={k}
            className={tab === k ? 'active' : ''}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>
      <ErrorAlert error={(patch.error ?? plan.error ?? aiSave.error) as ApiError | null} />
      {tab === 'account' ? (
        <>
          <Card title="Account">
            <dl className="kv">
              <dt>Workspace</dt>
              <dd>{ws.name}</dd>
              <dt>Email</dt>
              <dd>{me.email}</dd>
              <dt>Role</dt>
              <dd>{me.role}</dd>
              <dt>Subscription plan</dt>
              <dd>
                <Badge value={ws.plan} />
              </dd>
              {sub ? (
                <>
                  <dt>Subscription status</dt>
                  <dd>
                    <Badge value={sub.status} />
                    {billing.data?.usable === false ? (
                      <span className="muted"> (not usable)</span>
                    ) : null}
                  </dd>
                  <dt>
                    {sub.status === 'cancelled'
                      ? 'Ends on'
                      : sub.status === 'trialing'
                        ? 'Trial ends on'
                        : 'Renews on'}
                  </dt>
                  <dd>{fmtDate(sub.renewsAt)}</dd>
                </>
              ) : null}
            </dl>
            {sub?.status === 'past_due' ? (
              <div className="alert alert-error" role="alert">
                Your last payment failed. Update your card to keep automations running; access ends
                three days after the renewal date.
              </div>
            ) : null}
            {usePortal ? (
              <div className="alert" role="status">
                Change your plan from{' '}
                {portalHint ? <a href={portalHint}>Manage payment</a> : 'Manage payment'}; it
                prorates instead of charging twice.
              </div>
            ) : null}
            <ErrorAlert
              error={
                ((usePortal ? null : planError) ?? cancel.error ?? portal.error) as ApiError | null
              }
            />
            {can('billing:manage') &&
            sub &&
            sub.status !== 'cancelled' &&
            sub.status !== 'pending' ? (
              <div className="actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => portal.mutate()}
                  disabled={portal.isPending}
                >
                  Manage payment
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    if (window.confirm('Cancel at the end of the current period?')) cancel.mutate();
                  }}
                  disabled={cancel.isPending}
                >
                  Cancel subscription
                </button>
              </div>
            ) : null}
            {can('workspace:delete') ? (
              <div className="danger-zone" style={{ marginTop: 16 }} data-testid="danger-zone">
                <h4>Danger zone</h4>
                <p className="muted">
                  Deleting the workspace removes every account, automation, contact, conversation
                  and report it owns and cancels the subscription at the payment provider at once
                  (no refund is issued here). There is no undo. Type <strong>{ws.name}</strong> to
                  confirm.
                </p>
                <ErrorAlert error={destroy.error as ApiError | null} />
                <div className="row">
                  <input
                    className="input"
                    aria-label="workspace name to confirm deletion"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={ws.name}
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={confirmName.trim() !== ws.name || destroy.isPending}
                    onClick={() => destroy.mutate()}
                  >
                    Delete workspace
                  </button>
                </div>
              </div>
            ) : null}
            {can('billing:manage') ? (
              <div className="tabs" style={{ marginTop: 16 }}>
                {(['monthly', 'yearly'] as const).map((i) => (
                  <button
                    type="button"
                    key={i}
                    className={interval === i ? 'active' : ''}
                    onClick={() => setInterval(i)}
                  >
                    {i === 'monthly' ? 'Monthly' : 'Yearly (save ~17%)'}
                  </button>
                ))}
              </div>
            ) : null}
            {can('billing:manage') ? (
              <div className="grid grid-4" style={{ marginTop: 16 }}>
                {(plans.data?.items ?? [])
                  .filter((p) => p.plan !== 'trial')
                  .map((p) => (
                    <div
                      className={`card plan-card ${p.plan === ws.plan ? 'featured' : ''}`}
                      key={p.plan}
                    >
                      <div className="card-title">{p.name}</div>
                      <div className="price">
                        ${interval === 'yearly' ? p.yearlyUsdPerMonth : p.monthlyUsd}
                        <small>{interval === 'yearly' ? '/month, billed yearly' : '/month'}</small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={p.plan === ws.plan || plan.isPending}
                        onClick={() => plan.mutate(p.plan)}
                      >
                        {p.plan === ws.plan ? 'Current plan' : 'Change plan'}
                      </button>
                    </div>
                  ))}
              </div>
            ) : null}
            <button
              type="button"
              className="btn"
              style={{ marginTop: 16 }}
              onClick={() => void logout()}
            >
              Log out
            </button>
          </Card>
          <ChangePasswordCard />
        </>
      ) : null}
      {tab === 'control' ? (
        <Card title="Automation control">
          <div className="grid" style={{ gap: 10 }}>
            {toggle('automationsEnabled', 'Automations on')}
            {toggle('commentRepliesEnabled', 'Comment reply automation on')}
            {toggle('dmRepliesEnabled', 'DM reply automation on')}
          </div>
        </Card>
      ) : null}
      {tab === 'safety' ? (
        <>
          <Card title="Connected Instagram accounts">
            <DataTable>
              <table className="table">
                <tbody>
                  {(accounts.data?.items ?? []).map((a) => (
                    <tr key={a.id}>
                      <td>@{a.username}</td>
                      <td>
                        <Badge value={a.status} />
                      </td>
                      <td>
                        <Badge value={a.tokenStatus} />
                      </td>
                      <td className="actions">
                        {/* UI-61: Reconnect only where the accounts page offers it (UI-50) */}
                        {can('account:connect') && a.status !== 'connected' ? (
                          <Link className="btn btn-sm" to="/app/accounts">
                            Reconnect
                          </Link>
                        ) : null}{' '}
                        {can('account:read') ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => health.mutate(a.id)}
                          >
                            Test connection
                          </button>
                        ) : null}{' '}
                        {can('account:manage') && a.status === 'connected' ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              if (
                                window.confirm(`Disconnect @${a.username}? Its automations stop.`)
                              )
                                disconnect.mutate(a.id);
                            }}
                          >
                            Disconnect
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>
          </Card>
          <Card title="Comment limit per hour">
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                patch.mutate({ commentLimitPerHour: Number(limit) });
              }}
            >
              <Field label="Limit (0 = unlimited)">
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  disabled={!can('settings:manage')}
                />
              </Field>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!can('settings:manage') || patch.isPending}
              >
                Save
              </button>
            </form>
          </Card>
        </>
      ) : null}
      {tab === 'ai' ? (
        <Card title="AI configuration">
          <p className="muted">
            Use your own AI account to power AI comment replies and the AI reply flow node. Keys are
            encrypted at rest and never shown again.
          </p>
          <p>
            Current provider:{' '}
            {ws.aiProvider ? (
              <Badge value={ws.aiProvider} />
            ) : (
              <span className="muted">not configured</span>
            )}
          </p>
          {(ws.aiKeyProviders ?? []).length > 0 ? (
            <ul className="media-checks" aria-label="Stored AI keys">
              {PROVIDERS.filter(([v]) => ws.aiKeyProviders?.includes(v)).map(([v, l]) => (
                <li key={v} className="row" style={{ alignItems: 'center', gap: 8 }}>
                  <strong>{l}</strong>
                  <Badge value={ws.aiProvider === v ? 'active' : 'key stored'} />
                  {ws.aiProvider !== v ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!can('settings:manage') || aiSave.isPending}
                      onClick={() => aiSave.mutate({ provider: v })}
                    >
                      Use
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={!can('settings:manage') || aiSave.isPending}
                    onClick={() => aiSave.mutate({ provider: v, apiKey: null })}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              aiSave.mutate({ provider: ai.provider, apiKey: ai.apiKey });
            }}
          >
            <div className="row">
              <Field label="Provider">
                <select
                  className="input"
                  value={ai.provider}
                  onChange={(e) => setAi({ ...ai, provider: e.target.value })}
                >
                  {PROVIDERS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Secret key">
                <input
                  className="input mono"
                  type="password"
                  required
                  value={ai.apiKey}
                  onChange={(e) => setAi({ ...ai, apiKey: e.target.value })}
                />
              </Field>
            </div>
            <div className="actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!can('settings:manage') || aiSave.isPending}
              >
                Save key
              </button>
            </div>
          </form>
        </Card>
      ) : null}
    </>
  );
}
